import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  compareHandoffV1RunsCompareHandoffGet,
  compareStoreDiffV1RunsCompareStoreDiffGet,
  runStoreHullsV1RunsRunIdStoreHullsGet,
} from "../api/generated/qcsControlPlane";
import type { StoreHandoff, StoreHull, StoreProbe } from "../api/generated/model";
import { fmtInt } from "../api/labels";
import { Badge, Button, Panel } from "@akaike/ui";

// "What changed between the two runs" as a plain-language diff report, the readable
// counterpart to the maps. Every store-level change is one row: appeared / disappeared /
// shrank / grew, with a spelled-out verdict (for disappear/appear, whether the other run
// actually probed the area -> genuine vs inconclusive). The map is demoted to a locator:
// each row's "locate" button flies the store map to it.
//
// NOTE: intentionally NOT migrated to the shared <DataTable>. This is a bespoke
// hierarchical diff (grouped takeover parent/child sub-rows + narrative verdict
// cells), already filtered by change-type chips and sorted by magnitude: it is a
// report, not a flat sortable grid, so the data-grid model doesn't fit it.

// UI accent tokens (change-type colours). Kept as CSS vars so they stay
// theme/mode-correct wherever they feed an inline color / borderColor.
const APPEARED = "var(--ok)";
const DISAPPEARED = "var(--crit)";
const SHRANK = "var(--warn)";
const GREW = "var(--info)";

type ChangeType = "appeared" | "disappeared" | "shrank" | "grew";
type Filter = "all" | ChangeType;

const TYPE_COLOR: Record<ChangeType, string> = {
  appeared: APPEARED,
  disappeared: DISAPPEARED,
  shrank: SHRANK,
  grew: GREW,
};
const TYPE_LABEL: Record<ChangeType, string> = {
  appeared: "appeared",
  disappeared: "disappeared",
  shrank: "shrank",
  grew: "grew",
};

type Verdict = { tag: string; detail: string };
function probeVerdict(type: "appeared" | "disappeared", p: StoreProbe | undefined): Verdict {
  const other = type === "disappeared" ? "B" : "A";
  if (!p || p.probe_pts === 0)
    return {
      tag: "not scanned",
      detail: `run ${other} didn't probe here: inconclusive`,
    };
  if (p.serv_pts === 0)
    return {
      tag: "area dark",
      detail: `probed in ${other}, nothing serviceable: genuine`,
    };
  return {
    tag: "served by others",
    detail: `area still serviceable in ${other}, this store dropped`,
  };
}

type Row = {
  id: string;
  name: string;
  type: ChangeType;
  lat: number;
  lon: number;
  cellsA: number | null;
  cellsB: number | null;
  pct: number | null; // footprint % change (shrank/grew)
  verdict: Verdict | null;
  magnitude: number; // for sorting: cells gained/lost
};

// Material footprint change threshold for common stores.
const MIN_CELL_DELTA = 3;
const MIN_PCT = 25;

type HandoffLookup = {
  removed: Map<string, StoreHandoff>;
  shrank: Map<string, StoreHandoff>;
  absorbed: Map<string, string[]>;
};

function coveredByText(h: StoreHandoff | undefined, coverTag: (id: string) => string): string | null {
  if (!h || h.covered_by.length === 0) return null;
  const top = h.covered_by
    .slice(0, 2)
    .map((c) => `${c.store_id}${coverTag(c.store_id)} (+${c.cells})`)
    .join(", ");
  const more = h.covered_by.length > 2 ? ` +${h.covered_by.length - 2} more` : "";
  return `${top}${more}`;
}

// The verdict cell: handoff-aware. For disappeared/shrank it says who took over the lost
// area (or that nobody did); for grew/appeared it says whose area it absorbed.
function verdictCell(r: Row, hand: HandoffLookup, coverTag: (id: string) => string) {
  if (r.type === "disappeared") {
    const h = hand.removed.get(r.id);
    const cover = coveredByText(h, coverTag);
    if (cover)
      return (
        <>
          <b style={{ color: GREW }}>taken over</b>
          {": "}
          <span className="faint">
            {h?.covered_cells}/{h?.lost_cells} cells now served by {cover}
          </span>
        </>
      );
    return r.verdict ? (
      <>
        <b>{r.verdict.tag}</b>
        {": "}
        <span className="faint">
          {r.verdict.detail}
          {h ? `; 0/${h.lost_cells} cells picked up by others` : ""}
        </span>
      </>
    ) : null;
  }
  if (r.type === "shrank") {
    const h = hand.shrank.get(r.id);
    const cover = coveredByText(h, coverTag);
    if (h && cover)
      return (
        <>
          <b style={{ color: SHRANK }}>shrank</b>
          {": "}
          <span className="faint">
            lost {h.lost_cells} cells; {h.covered_cells} now served by {cover}
          </span>
        </>
      );
    if (h)
      return <span className="faint">lost {h.lost_cells} cells, not picked up by other stores</span>;
    return <span className="faint">footprint shrank</span>;
  }
  // appeared / grew: did this store absorb others' area?
  const sources = hand.absorbed.get(r.id);
  const took =
    sources && sources.length
      ? `${sources.slice(0, 2).join(", ")}${sources.length > 2 ? ` +${sources.length - 2} more` : ""}`
      : null;
  if (r.type === "grew")
    return took ? (
      <>
        <b style={{ color: GREW }}>absorbed</b>
        {": "}
        <span className="faint">now covers area from {took}</span>
      </>
    ) : (
      <span className="faint">footprint grew</span>
    );
  // appeared
  return (
    <>
      {r.verdict && (
        <>
          <b>{r.verdict.tag}</b>: <span className="faint">{r.verdict.detail}</span>
        </>
      )}
      {took && <span className="faint">{r.verdict ? "; " : ""}took over area from {took}</span>}
    </>
  );
}

export function RunChangesTable({
  runIdA,
  runIdB,
  onLocate,
}: {
  runIdA: string;
  runIdB: string;
  onLocate: (id: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [grouped, setGrouped] = useState(false);

  const hullsA = useQuery({
    queryKey: ["store-hulls", runIdA],
    queryFn: () => runStoreHullsV1RunsRunIdStoreHullsGet(runIdA),
  });
  const hullsB = useQuery({
    queryKey: ["store-hulls", runIdB],
    queryFn: () => runStoreHullsV1RunsRunIdStoreHullsGet(runIdB),
  });
  const probeQ = useQuery({
    queryKey: ["store-diff-probe", runIdA, runIdB],
    queryFn: () =>
      compareStoreDiffV1RunsCompareStoreDiffGet({
        run_a: runIdA,
        run_b: runIdB,
      }),
  });
  const handoffQ = useQuery({
    queryKey: ["store-handoff", runIdA, runIdB],
    queryFn: () => compareHandoffV1RunsCompareHandoffGet({ run_a: runIdA, run_b: runIdB }),
  });
  // Lookups: per disappeared/shrank store, who took over; and the reverse: per
  // covering store, which stores it absorbed (so a grown store can say what it ate).
  const handoff = useMemo(() => {
    const removed = new Map((handoffQ.data?.removed ?? []).map((h) => [h.store_id, h]));
    const shrank = new Map((handoffQ.data?.shrank ?? []).map((h) => [h.store_id, h]));
    const absorbed = new Map<string, string[]>(); // coveringId -> [source ids]
    for (const h of [...(handoffQ.data?.removed ?? []), ...(handoffQ.data?.shrank ?? [])]) {
      for (const c of h.covered_by) {
        const list = absorbed.get(c.store_id) ?? [];
        list.push(h.store_id);
        absorbed.set(c.store_id, list);
      }
    }
    return { removed, shrank, absorbed };
  }, [handoffQ.data]);

  const rows = useMemo<Row[]>(() => {
    const aById = new Map((hullsA.data?.stores ?? []).map((s) => [s.store_id, s]));
    const bById = new Map((hullsB.data?.stores ?? []).map((s) => [s.store_id, s]));
    const removedProbe = new Map((probeQ.data?.removed ?? []).map((p) => [p.store_id, p]));
    const addedProbe = new Map((probeQ.data?.added ?? []).map((p) => [p.store_id, p]));
    const out: Row[] = [];

    const pos = (s: StoreHull) => ({ lat: s.lat, lon: s.lon });

    for (const [id, s] of bById) {
      if (!aById.has(id)) {
        out.push({
          id,
          name: s.name,
          type: "appeared",
          ...pos(s),
          cellsA: null,
          cellsB: s.cells,
          pct: null,
          verdict: probeVerdict("appeared", addedProbe.get(id)),
          magnitude: s.cells,
        });
      }
    }
    for (const [id, s] of aById) {
      if (!bById.has(id)) {
        out.push({
          id,
          name: s.name,
          type: "disappeared",
          ...pos(s),
          cellsA: s.cells,
          cellsB: null,
          pct: null,
          verdict: probeVerdict("disappeared", removedProbe.get(id)),
          magnitude: s.cells,
        });
        continue;
      }
      // common: material footprint change?
      const b = bById.get(id) as StoreHull;
      const delta = b.cells - s.cells;
      const pct = s.cells > 0 ? Math.round((delta / s.cells) * 100) : 0;
      if (Math.abs(delta) >= MIN_CELL_DELTA && Math.abs(pct) >= MIN_PCT) {
        out.push({
          id,
          name: b.name || s.name,
          type: delta < 0 ? "shrank" : "grew",
          ...pos(b),
          cellsA: s.cells,
          cellsB: b.cells,
          pct,
          verdict: null,
          magnitude: Math.abs(delta),
        });
      }
    }
    out.sort((x, y) => y.magnitude - x.magnitude);
    return out;
  }, [hullsA.data, hullsB.data, probeQ.data]);

  const counts = useMemo(() => {
    const c = { appeared: 0, disappeared: 0, shrank: 0, grew: 0 };
    for (const r of rows) c[r.type]++;
    return c;
  }, [rows]);
  const changeTypeById = useMemo(() => new Map(rows.map((r) => [r.id, r.type])), [rows]);

  // Grouping: each vacated (disappeared/shrank) store + the stores that took it over.
  const groups = useMemo(() => {
    const gs: {
      parent: Row;
      children: { store_id: string; name: string; cells: number }[];
    }[] = [];
    for (const r of rows) {
      if (r.type !== "disappeared" && r.type !== "shrank") continue;
      const h = r.type === "disappeared" ? handoff.removed.get(r.id) : handoff.shrank.get(r.id);
      if (h && h.covered_by.length) gs.push({ parent: r, children: h.covered_by });
    }
    gs.sort((a, b) => b.children.length - a.children.length);
    return gs;
  }, [rows, handoff]);
  const otherRows = useMemo(() => {
    const parentIds = new Set(groups.map((g) => g.parent.id));
    const takerIds = new Set(groups.flatMap((g) => g.children.map((c) => c.store_id)));
    return rows.filter((r) => !parentIds.has(r.id) && !takerIds.has(r.id));
  }, [rows, groups]);

  // Short label for a taker's own change type.
  const childTag = (id: string): string => {
    const t = changeTypeById.get(id);
    return t === "grew" ? "grew" : t === "appeared" ? "new" : "existing";
  };
  const childColor = (id: string): string =>
    TYPE_COLOR[(changeTypeById.get(id) as ChangeType) ?? "grew"] ?? GREW;

  // Short tag for a covering store: note if it itself grew / is new this run.
  const coverTag = (id: string): string => {
    const t = changeTypeById.get(id);
    return t === "grew" ? " (grew)" : t === "appeared" ? " (new)" : "";
  };

  const shown = filter === "all" ? rows : rows.filter((r) => r.type === filter);
  const loading = hullsA.isLoading || hullsB.isLoading || probeQ.isLoading;

  return (
    <Panel className="mb-3.5" style={{ gridColumn: "span 12" }}>
      <Panel.Head title="What changed · A → B" className="flex-wrap gap-2">
        <div className="flex-1" />
        <Button
          variant={grouped ? "default" : "outline"}
          size="sm"
          className="mr-1"
          style={{ fontSize: 11, padding: "4px 9px" }}
          onClick={() => setGrouped((g) => !g)}
          title="Group each vacated store with the stores that took over its area"
        >
          {grouped ? "▾ grouped" : "group takeovers"}
        </Button>
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {chip(filter, setFilter, "all", `all (${rows.length})`)}
          {chip(filter, setFilter, "appeared", `+${counts.appeared} appeared`, APPEARED)}
          {chip(filter, setFilter, "disappeared", `−${counts.disappeared} disappeared`, DISAPPEARED)}
          {chip(filter, setFilter, "shrank", `${counts.shrank} shrank`, SHRANK)}
          {chip(filter, setFilter, "grew", `${counts.grew} grew`, GREW)}
        </div>
      </Panel.Head>
      <Panel.Body tight>
        <Narrative counts={counts} loading={loading} />
        {(probeQ.data?.capped || handoffQ.data?.capped) && (
          <div className="muted" style={{ fontSize: 11, padding: "0 4px 8px" }}>
            Large comparison: showing the biggest changes; some verdicts and takeover
            links were truncated.
          </div>
        )}
        {grouped ? (
          <table className="tbl" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Store</th>
                <th style={{ textAlign: "right" }}>Cells</th>
                <th>Detail</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ fontSize: 12 }}>
                    {loading
                      ? "loading…"
                      : "No takeover groups, no vacated store had its area picked up by others."}
                  </td>
                </tr>
              )}
              {groups.map((g) => (
                <Fragment key={`g-${g.parent.id}`}>
                  <tr
                    style={{
                      background: "var(--panel-2, rgba(255,255,255,0.03))",
                    }}
                  >
                    <td>
                      <Badge variant="outline"
                        style={{
                          color: TYPE_COLOR[g.parent.type],
                          borderColor: TYPE_COLOR[g.parent.type],
                          fontSize: 10,
                        }}
                      >
                        {TYPE_LABEL[g.parent.type]}
                      </Badge>
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                        {g.parent.id}
                      </span>
                      {g.parent.name && (
                        <span className="faint" style={{ fontSize: 11, marginLeft: 6 }}>
                          {g.parent.name}
                        </span>
                      )}
                    </td>
                    <td className="num" style={{ textAlign: "right", fontSize: 11.5 }}>
                      {g.parent.cellsA ?? "—"} → {g.parent.cellsB ?? "—"}
                    </td>
                    <td className="faint" style={{ fontSize: 11 }}>
                      taken over by {g.children.length} store
                      {g.children.length > 1 ? "s" : ""}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button variant="outline" size="sm" style={{ fontSize: 10.5 }} onClick={() => onLocate(g.parent.id)}>
                        locate
                      </Button>
                    </td>
                  </tr>
                  {g.children.map((c) => (
                    <tr key={`g-${g.parent.id}-${c.store_id}`}>
                      <td style={{ paddingLeft: 18 }}>
                        <span className="faint" style={{ marginRight: 4 }}>
                          ↳
                        </span>
                        <Badge variant="outline"
                          style={{
                            color: childColor(c.store_id),
                            borderColor: childColor(c.store_id),
                            fontSize: 9.5,
                          }}
                        >
                          {childTag(c.store_id)}
                        </Badge>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 11, fontWeight: 600 }}>
                          {c.store_id}
                        </span>
                        {c.name && (
                          <span className="faint" style={{ fontSize: 10.5, marginLeft: 6 }}>
                            {c.name}
                          </span>
                        )}
                      </td>
                      <td
                        className="num"
                        style={{
                          textAlign: "right",
                          fontSize: 11,
                          color: childColor(c.store_id),
                        }}
                      >
                        +{c.cells}
                      </td>
                      <td className="faint" style={{ fontSize: 10.5 }}>
                        took over {c.cells} of {g.parent.id}'s cells
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Button variant="outline" size="sm" style={{ fontSize: 10 }} onClick={() => onLocate(c.store_id)}>
                          locate
                        </Button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {otherRows.length > 0 && (
                <tr>
                  <td colSpan={5} className="eyebrow" style={{ paddingTop: 12 }}>
                    OTHER CHANGES (no takeover link)
                  </td>
                </tr>
              )}
              {otherRows.map((r) => (
                <tr key={`o-${r.type}-${r.id}`}>
                  <td>
                    <Badge variant="outline"
                      style={{
                        color: TYPE_COLOR[r.type],
                        borderColor: TYPE_COLOR[r.type],
                        fontSize: 10,
                      }}
                    >
                      {TYPE_LABEL[r.type]}
                    </Badge>
                  </td>
                  <td>
                    <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                      {r.id}
                    </span>
                    {r.name && (
                      <span className="faint" style={{ fontSize: 11, marginLeft: 6 }}>
                        {r.name}
                      </span>
                    )}
                  </td>
                  <td className="num" style={{ textAlign: "right", fontSize: 11.5 }}>
                    {r.cellsA ?? "—"} → {r.cellsB ?? "—"}
                  </td>
                  <td style={{ fontSize: 11 }}>{verdictCell(r, handoff, coverTag)}</td>
                  <td style={{ textAlign: "right" }}>
                    <Button variant="outline" size="sm" style={{ fontSize: 10.5 }} onClick={() => onLocate(r.id)}>
                      locate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : shown.length === 0 ? (
          <div className="muted" style={{ fontSize: 12.5, padding: "10px 4px" }}>
            {loading ? "loading changes…" : "No changes of this kind between the two runs."}
          </div>
        ) : (
          <table className="tbl" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Change</th>
                <th>Store</th>
                <th style={{ textAlign: "right" }}>Cells A→B</th>
                <th>Verdict</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={`${r.type}-${r.id}`}>
                  <td>
                    <Badge variant="outline"
                      style={{
                        color: TYPE_COLOR[r.type],
                        borderColor: TYPE_COLOR[r.type],
                        fontSize: 10,
                      }}
                    >
                      {TYPE_LABEL[r.type]}
                    </Badge>
                  </td>
                  <td>
                    <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                      {r.id}
                    </span>
                    {r.name && (
                      <span className="faint" style={{ fontSize: 11, marginLeft: 6 }}>
                        {r.name}
                      </span>
                    )}
                  </td>
                  <td className="num" style={{ textAlign: "right", fontSize: 11.5 }}>
                    {r.cellsA ?? "—"} → {r.cellsB ?? "—"}
                    {r.pct != null && (
                      <span style={{ color: TYPE_COLOR[r.type], marginLeft: 6 }}>
                        ({r.pct > 0 ? "+" : ""}
                        {r.pct}%)
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    {verdictCell(r, handoff, coverTag)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Button variant="outline" size="sm" style={{ fontSize: 10.5 }} onClick={() => onLocate(r.id)}>
                      locate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel.Body>
    </Panel>
  );
}

function Narrative({
  counts,
  loading,
}: {
  counts: {
    appeared: number;
    disappeared: number;
    shrank: number;
    grew: number;
  };
  loading: boolean;
}) {
  const net = counts.appeared - counts.disappeared;
  return (
    <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, padding: "2px 4px 10px" }}>
      {loading ? (
        "Computing the diff…"
      ) : (
        <>
          Going from <b>A → B</b>:{" "}
          <span style={{ color: APPEARED }}>{fmtInt(counts.appeared)} stores appeared</span>,{" "}
          <span style={{ color: DISAPPEARED }}>{fmtInt(counts.disappeared)} disappeared</span>{" "}
          (net {net >= 0 ? "+" : ""}
          {fmtInt(net)});{" "}
          <span style={{ color: SHRANK }}>{fmtInt(counts.shrank)} shrank</span> and{" "}
          <span style={{ color: GREW }}>{fmtInt(counts.grew)} grew</span> their serviceable area.
          Use the verdict column to tell genuine drops (area probed &amp; dark) from
          inconclusive ones (the other run never probed there).
        </>
      )}
    </div>
  );
}

function chip(
  filter: Filter,
  setFilter: (f: Filter) => void,
  f: Filter,
  label: string,
  color?: string,
) {
  return (
    <Button
      variant={filter === f ? "default" : "outline"}
      size="sm"
      style={{
        fontSize: 11,
        padding: "4px 9px",
        borderColor: color,
        color: filter === f ? undefined : color,
      }}
      onClick={() => setFilter(f)}
    >
      {label}
    </Button>
  );
}
